import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAdmin, isDistributorAdmin } from '../utils/roles';
import { useT } from '../i18n/LanguageContext';

/**
 * SETTINGS SUB-NAVIGATION (8/28/26).
 *
 * Content and Deletion Requests came off the sidebar and became tabs here. Both are administered a
 * handful of times a year and each was holding a top-level nav slot.
 *
 * ============================================================================================
 * THEY KEPT THEIR OWN TOP-LEVEL ROUTES, AND THAT IS A SECURITY DECISION, NOT LAZINESS.
 *
 * App.js derives every route guard from repMayVisit() rather than opting in per route, and
 * repMayVisit matches a whitelisted path OR ANY PATH BENEATH IT:
 *
 *     path === allowed || path.startsWith(`${allowed}/`)      <- roles.js
 *
 * '/settings' is rep-whitelisted. So renaming these to '/settings/content' and
 * '/settings/deletion-requests' would make repMayVisit return TRUE for them, guardedElement would
 * stop wrapping them in RequireAdmin, and two admin-only pages would become rep-reachable - a
 * fail-open regression produced by nothing but a URL change. Leaving them at '/content' and
 * '/deletion-requests' leaves that derivation exactly as it was.
 *
 * SettingsTabs.test.js pins this: it asserts repMayVisit is false for every adminOnly tab path, so
 * a future move under /settings fails the suite instead of shipping.
 * ============================================================================================
 *
 * adminOnly below is COSMETIC, like everything driven by roles.js. RequireAdmin on the route and
 * @Roles('admin') on the server are the actual gate.
 */
export const SETTINGS_TABS = [
  { label: 'Account', path: '/settings' },
  { label: 'Content', path: '/content', adminOnly: true },
  { label: 'Deletion Requests', path: '/deletion-requests', adminOnly: true },
];

export default function SettingsTabs() {
  const { user } = useAuth();
  const t = useT();
  const tabs =
    isAdmin(user) && !isDistributorAdmin(user)
      ? SETTINGS_TABS
      : SETTINGS_TABS.filter((tab) => !tab.adminOnly);

  // A rep has exactly one of these, and one tab is not a tab bar. Rendering nothing leaves their
  // Settings page looking precisely as it did before this change.
  if (tabs.length < 2) return null;

  return (
    <div className="flex items-center gap-1 border-b border-[#e2e8f0] mb-6">
      {tabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          className={({ isActive }) =>
            `px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
              isActive
                ? 'border-[#0089ff] text-[#0089ff]'
                : 'border-transparent text-[#64748b] hover:text-[#0f172a]'
            }`
          }
        >
          {t(tab.label)}
        </NavLink>
      ))}
    </div>
  );
}
